import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BadgeCheck, Clock, Home, Receipt, ShieldCheck, type LucideIcon } from 'lucide-react';
import { APP_CONFIG } from '../config/app';

interface Advantage {
  icon: LucideIcon;
  title: string;
  description: string;
}

const ADVANTAGES: readonly Advantage[] = [
  {
    icon: Home,
    title: 'À domicile ou sur site',
    description: 'Je me déplace chez vous à Viry et autour de Genève (Pays de Gex, Haute-Savoie).',
  },
  {
    icon: Receipt,
    title: 'Prix annoncé avant d’intervenir',
    description: 'Diagnostic gratuit, devis clair, aucune surprise.',
  },
  {
    icon: ShieldCheck,
    title: 'Garantie 90 jours',
    description: 'Pièces de qualité et travail garanti. Problème non résolu = remboursé.',
  },
  {
    icon: Clock,
    title: 'Réponse rapide, 7j/7',
    description: 'Réponse en moins de 2h, intervention souvent le jour même.',
  },
];

/** Avantages transverses aux trois activités. */
const WhyUs: React.FC = () => (
  <section id="avantages" className="section-spacing border-t border-line bg-canvas">
    <div className="container-site">
      <div className="section-title-spacing text-center">
        <div className="section-subtitle-badge">
          <BadgeCheck size={14} aria-hidden />
          <span>Mes engagements</span>
        </div>
        <h2 className="heading-section">
          Pourquoi <span className="text-accent-soft">SOS IT Services</span>&nbsp;?
        </h2>
      </div>

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
        {ADVANTAGES.map(({ icon: Icon, title, description }, index) => (
          <motion.li
            key={title}
            className="card flex items-center gap-3 p-4 md:flex-col md:items-start md:p-5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4, delay: index * 0.08, ease: 'easeOut' }}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-2 text-accent-soft">
              <Icon size={20} aria-hidden />
            </span>
            <h3 className="text-base font-bold leading-tight text-fg">{title}</h3>
            <p className="hidden text-sm leading-relaxed text-fg-muted md:block">{description}</p>
          </motion.li>
        ))}
      </ul>

      <p className="mt-8 text-center">
        <Link
          to={APP_CONFIG.routes.reparationTelephoneTarifs}
          className="group inline-flex min-h-11 items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-accent-soft"
        >
          Voir tous les tarifs
          <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
        </Link>
      </p>
    </div>
  </section>
);

export default WhyUs;
